import { useCallback, useEffect, useRef, useState } from "react";

import { API_URL } from "../config/connect";

/**
 * Оператор жағындағы WebRTC дауыстық қоңырау.
 *
 * Сигнал сол `chat.py` сокеті арқылы жүреді (`voice_call.py`), ал медиа —
 * браузердің `RTCPeerConnection`-ы арқылы тікелей. Сервер тек SDP/ICE
 * кадрларын екі жаққа жеткізеді.
 *
 * Қоңырау «Қолдау» АТЫНАН кетеді: `call_invite`-қа `as_actor: "support"`
 * жіберіледі, сонда клиенттің телефонында оператордың жеке аты емес, қолдау
 * қызметі шырылдайды әрі қоңырау жазбасы қолдау чатына түседі.
 *
 * Күйлер: idle → ringing → connecting → active → (ended)
 *
 * Шақырушы ғана — кіріс қоңырауды қабылдау әзірге жоқ: клиент қолдауға
 * қоңырау шалса, ол 31-ші id-ге барады, ал оның сокеті жоқ. Ол үшін серверде
 * актордан кезекші операторға бағыттау керек (бөлек жұмыс).
 */

const STUN_FALLBACK = [
  { urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] },
];

/** `GET /api/v1/turn/credentials` → RTCIceServer тізімі.
 *  404/401 болса (TURN бапталмаған) — үнсіз STUN-ға түсеміз, iOS та солай. */
async function fetchIceServers() {
  try {
    const token = localStorage.getItem("google_id_token");
    const res = await fetch(`${API_URL}/api/v1/turn/credentials`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return STUN_FALLBACK;
    const data = await res.json();
    const urls = data?.turn?.urls;
    if (!Array.isArray(urls) || urls.length === 0) return STUN_FALLBACK;
    return [
      ...STUN_FALLBACK,
      {
        urls,
        username: data.turn.username,
        credential: data.turn.credential,
      },
    ];
  } catch {
    return STUN_FALLBACK;
  }
}

export default function useVoiceCall(socketRef) {
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [muted, setMuted] = useState(false);
  const [seconds, setSeconds] = useState(0);

  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const audioRef = useRef(null);
  const callIdRef = useRef(null);
  // `call:accepted` кейде `call:peer_ice`-тен кейін келеді — сол аралықта
  // түскен кандидаттарды жинап тұрамыз, әйтпесе олар жоғалады.
  const pendingIceRef = useRef([]);

  const cleanup = useCallback(() => {
    if (pcRef.current) {
      pcRef.current.onicecandidate = null;
      pcRef.current.ontrack = null;
      pcRef.current.onconnectionstatechange = null;
      pcRef.current.close();
      pcRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.srcObject = null;
    }
    pendingIceRef.current = [];
    callIdRef.current = null;
    setMuted(false);
    setSeconds(0);
  }, []);

  const hangup = useCallback(() => {
    const socket = socketRef.current;
    if (socket && callIdRef.current) {
      socket.emit("call_hangup", { call_id: callIdRef.current });
    }
    cleanup();
    setStatus("idle");
  }, [cleanup, socketRef]);

  /** Микрофонды алып, PeerConnection дайындайды. */
  const preparePeer = useCallback(async () => {
    const iceServers = await fetchIceServers();
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    localStreamRef.current = stream;

    const pc = new RTCPeerConnection({ iceServers });
    stream.getTracks().forEach((track) => pc.addTrack(track, stream));

    pc.ontrack = (e) => {
      if (audioRef.current) {
        audioRef.current.srcObject = e.streams[0];
        audioRef.current.play().catch(() => {
          /* автоойнату бұғатталса — қолданушы әрекетінен кейін қосылады */
        });
      }
    };
    pc.onicecandidate = (e) => {
      if (e.candidate && socketRef.current && callIdRef.current) {
        socketRef.current.emit("call_ice_candidate", {
          call_id: callIdRef.current,
          candidate: {
            candidate: e.candidate.candidate,
            sdpMid: e.candidate.sdpMid,
            sdpMLineIndex: e.candidate.sdpMLineIndex,
          },
        });
      }
    };
    pc.onconnectionstatechange = () => {
      const st = pc.connectionState;
      if (st === "connected") setStatus("active");
      if (st === "failed" || st === "disconnected") {
        setError("Байланыс үзілді");
        hangup();
      }
    };
    pcRef.current = pc;
    return pc;
  }, [hangup, socketRef]);

  const startCall = useCallback(
    async (targetUserId) => {
      const socket = socketRef.current;
      if (!socket || !targetUserId) return;
      setError(null);
      setStatus("ringing");
      try {
        await preparePeer();
        socket.emit("call_invite", {
          to_user_id: targetUserId,
          // Клиент «Қолдау» дегенді көруі керек, оператордың жеке атын емес.
          as_actor: "support",
        });
      } catch (e) {
        // Микрофонға рұқсат берілмеген жағдай да осында түседі.
        setError(e.name === "NotAllowedError" ? "Микрофонға рұқсат жоқ" : e.message);
        cleanup();
        setStatus("idle");
      }
    },
    [cleanup, preparePeer, socketRef],
  );

  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const next = !muted;
    stream.getAudioTracks().forEach((t) => {
      t.enabled = !next;
    });
    setMuted(next);
  }, [muted]);

  // Сигнал оқиғалары
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return undefined;

    const onAck = (d) => {
      callIdRef.current = d.call_id;
      if (!d.callee_online && !d.push_sent) setError("Қолданушы қолжетімсіз");
    };

    const onAccepted = async () => {
      setStatus("connecting");
      const pc = pcRef.current;
      if (!pc) return;
      try {
        const offer = await pc.createOffer({ offerToReceiveAudio: true });
        await pc.setLocalDescription(offer);
        socket.emit("call_offer", {
          call_id: callIdRef.current,
          sdp: { type: "offer", sdp: offer.sdp },
        });
      } catch (e) {
        setError(e.message);
        hangup();
      }
    };

    const onPeerAnswer = async (d) => {
      const pc = pcRef.current;
      if (!pc || !d?.sdp) return;
      try {
        await pc.setRemoteDescription(
          new RTCSessionDescription({ type: d.sdp.type || "answer", sdp: d.sdp.sdp }),
        );
        // Ерте келген кандидаттарды енді қосуға болады.
        for (const c of pendingIceRef.current) {
          await pc.addIceCandidate(new RTCIceCandidate(c)).catch(() => {});
        }
        pendingIceRef.current = [];
      } catch (e) {
        setError(e.message);
      }
    };

    const onPeerIce = async (d) => {
      const pc = pcRef.current;
      if (!pc || !d?.candidate) return;
      if (!pc.remoteDescription) {
        pendingIceRef.current.push(d.candidate);
        return;
      }
      await pc.addIceCandidate(new RTCIceCandidate(d.candidate)).catch(() => {});
    };

    const onRejected = (d) => {
      const reasons = {
        callee_offline: "Қолданушы желіде емес",
        declined: "Қоңырау қабылданбады",
        busy: "Қолданушы бос емес",
      };
      setError(reasons[d?.reason] || "Қоңырау қабылданбады");
      cleanup();
      setStatus("idle");
    };

    const onHungup = () => {
      cleanup();
      setStatus("idle");
    };

    const onCallError = (d) => {
      setError(d?.error || "Қоңырау қатесі");
      cleanup();
      setStatus("idle");
    };

    socket.on("call:invite_ack", onAck);
    socket.on("call:accepted", onAccepted);
    socket.on("call:peer_answer", onPeerAnswer);
    socket.on("call:peer_ice", onPeerIce);
    socket.on("call:rejected", onRejected);
    socket.on("call:hungup", onHungup);
    socket.on("call:error", onCallError);

    return () => {
      socket.off("call:invite_ack", onAck);
      socket.off("call:accepted", onAccepted);
      socket.off("call:peer_answer", onPeerAnswer);
      socket.off("call:peer_ice", onPeerIce);
      socket.off("call:rejected", onRejected);
      socket.off("call:hungup", onHungup);
      socket.off("call:error", onCallError);
    };
  }, [cleanup, hangup, socketRef, status]);

  // Сөйлесу уақыты
  useEffect(() => {
    if (status !== "active") return undefined;
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [status]);

  // Компонент жабылғанда тұтқаны қоямыз — әйтпесе микрофон қосулы қалады.
  useEffect(() => cleanup, [cleanup]);

  return {
    status,
    error,
    muted,
    seconds,
    audioRef,
    startCall,
    hangup,
    toggleMute,
    clearError: () => setError(null),
  };
}
